"use client";

import { Bar } from "@visx/shape";
import { motion } from "framer-motion";
import React, { useId } from "react";
import { useChartContext } from "../../../context/ChartContext";
import { BaseDataPoint } from "../../../utils/charts";

interface BarSeriesProps {
  /**
   * The points to draw.
   *
   * Optional ONLY inside a streaming chart, where the points do not exist until
   * the reader looks at a window: there the child names its stream with
   * `seriesKey` and the chart supplies `data` before this component renders. In
   * an ordinary chart it is required in practice — a series with neither
   * renders nothing rather than throwing.
   */
  data?: BaseDataPoint[];
  /**
   * Which stream this series draws, in a chart with a streaming `source`.
   *
   * Defaults to `"line"`, the primary stream — the same default `AreaSeries`
   * and `LineSeries` use, so the same child serves both sources. Ignored
   * entirely by a chart that holds its own data.
   */
  seriesKey?: string;
  color?: string;
  label?: string;
  opacity?: number;
  categoryColors?: Record<string, string>;
  categoryLabels?: Record<string, string>;
}

/**
 * BarSeries
 *
 * Chart component for rendering bar/column charts.
 * Displays data as vertical bars with customizable colors, opacity, and category-based coloring.
 */
export const BarSeries: React.FC<BarSeriesProps> = ({
  data,
  color = "var(--color-viz-default)",
  opacity = 1,
  categoryColors = {},
}) => {
  const { xScale, yScale, animationSettings } = useChartContext();
  const clipPathId = useId();

  // Get the exact plotting area bounds from the scales
  const xRange = xScale.range();
  const yRange = yScale.range();

  /*
   * 🔴 `data` is absent for one render inside a streaming chart.
   *
   * The streaming branch clones each series child with its resolved points, but
   * this component's own type must tolerate the gap — spreading `undefined`
   * here would throw before the clone ever lands, taking the whole chart with
   * it rather than drawing nothing.
   */
  const sortedData = [...(data ?? [])].sort((a, b) => a.xValue.getTime() - b.xValue.getTime());

  // Early return if no data (e.g., after zoom filtering)
  if (sortedData.length === 0) {
    return null;
  }

  // Calculate the time interval between points
  const timeInterval =
    sortedData.length > 1 ? sortedData[1].xValue.getTime() - sortedData[0].xValue.getTime() : 15 * 60000; // Default to 15 minutes if only one point

  // Calculate bar width based on the time interval
  const barWidth = Math.abs(
    xScale(new Date(sortedData[0].xValue.getTime() + timeInterval)) - xScale(sortedData[0].xValue)
  );

  return (
    <>
      <defs>
        <clipPath id={clipPathId}>
          <rect x={xRange[0]} y={yRange[1]} width={xRange[1] - xRange[0]} height={yRange[0] - yRange[1]} />
        </clipPath>
      </defs>

      <g clipPath={`url(#${clipPathId})`}>
        <motion.g
          initial={{ scaleY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{
            duration: animationSettings.duration,
            ease: animationSettings.ease as "easeInOut",
          }}
          style={{
            transformOrigin: `0 ${yScale(0)}px`,
            isolation: "isolate",
          }}
        >
          {sortedData.map((d, i) => {
            const x = xScale(d.xValue);
            // When chartYValue/baselineValue are set (stacked charts), draw the
            // bar between those coordinates instead of zero→yValue. This keeps
            // tooltips/exports honest (yValue = segment magnitude) while drawing
            // the visual stack at the correct cumulative offset.
            const drawYValue = d.chartYValue ?? d.yValue;
            const baseline = d.baselineValue ?? 0;
            const y = yScale(drawYValue);
            const zeroY = yScale(baseline);

            // Bars extend from the baseline to the data value
            // Positive values: bar goes upward from baseline (y < zeroY in SVG coords)
            // Negative values: bar goes downward from baseline (y > zeroY in SVG coords)
            const barY = Math.min(y, zeroY);
            const barHeight = Math.abs(y - zeroY);

            // Calculate this bar's width based on the next point
            const nextPoint = sortedData[i + 1];
            const thisBarWidth = nextPoint ? Math.abs(xScale(nextPoint.xValue) - x) : barWidth;

            // Get color based on category if available
            const barColor = d.category && categoryColors[d.category] ? categoryColors[d.category] : color;

            return (
              <Bar
                key={d.xValue.getTime()}
                x={x - thisBarWidth / 2}
                y={barY}
                width={thisBarWidth}
                height={barHeight}
                fill={barColor}
                opacity={opacity}
                rx={1}
              />
            );
          })}
        </motion.g>
      </g>
    </>
  );
};
